// A standalone, unauthenticated page: opened when a phone scans the QR code
// shown in Pharmacy -> Procurement -> Goods Receipt. Rendered directly by
// main.tsx (before the login gate) whenever the URL carries
// ?mobile_upload_procurement=<token> — modeled directly on
// MobileUploadPage.tsx, just for a vendor invoice (branch-scoped) instead of
// a patient document.
import { useState, useEffect, useRef, type CSSProperties } from 'react';
import { useBranding } from '../hooks/useBranding';
import { getMobileUploadProcurementInfo, submitMobileUploadProcurement } from '../api';

interface MobileUploadProcurementInfo {
  poNumber?: string | null;
}

interface UploadEntry {
  name: string;
  status: 'uploading' | 'done' | 'error';
  message?: string;
}

export default function MobileUploadProcurementPage({ token }: { token: string }) {
  const { branding } = useBranding();
  const [info, setInfo]       = useState<MobileUploadProcurementInfo | null>(null);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(true);
  const [uploads, setUploads] = useState<UploadEntry[]>([]);
  const fref = useRef<HTMLInputElement>(null);

  const load = () => {
    setLoading(true);
    getMobileUploadProcurementInfo(token)
      .then((data: MobileUploadProcurementInfo) => { setInfo(data); setError(''); })
      .catch(e => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFiles = async (fileList: FileList | null) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    for (const file of files) {
      const entry: UploadEntry = { name: file.name, status: 'uploading' };
      setUploads(u => [entry, ...u]);
      try {
        await submitMobileUploadProcurement(token, file);
        setUploads(u => u.map(x => x === entry ? { ...x, status: 'done' } : x));
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        setUploads(u => u.map(x => x === entry ? { ...x, status: 'error', message } : x));
      }
    }
    if (fref.current) fref.current.value = '';
  };

  const wrap: CSSProperties = { minHeight: '100vh', background: '#F3F4F6', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 16px', fontFamily: "'DM Sans','Segoe UI',system-ui,sans-serif" };
  const card: CSSProperties = { width: '100%', maxWidth: 420, background: '#fff', borderRadius: 14, padding: 22, boxShadow: '0 1px 3px rgba(0,0,0,.08)' };

  if (loading) {
    return <div style={wrap}><div style={card}><div style={{ textAlign: 'center', color: '#6B7280', fontSize: 14, padding: 20 }}>Loading…</div></div></div>;
  }

  if (error || !info) {
    return (
      <div style={wrap}>
        <div style={card}>
          <div style={{ textAlign: 'center', fontSize: 32, marginBottom: 8 }}>⚠️</div>
          <div style={{ textAlign: 'center', fontWeight: 600, color: '#991B1B', marginBottom: 6 }}>Upload link unavailable</div>
          <div style={{ textAlign: 'center', fontSize: 13, color: '#6B7280' }}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          {branding.logo_url && <img src={branding.logo_url} alt="" style={{ height: 40, objectFit: 'contain', marginBottom: 8 }} />}
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy, #0B1D33)' }}>{branding.hospital_name}</div>
        </div>

        <div style={{ background: '#F0FDF4', border: '1px solid #A7F3D0', borderRadius: 10, padding: '10px 14px', marginBottom: 16, textAlign: 'center' }}>
          <div style={{ fontSize: 13, color: '#065F46', fontWeight: 600 }}>Uploading vendor invoice</div>
          {info.poNumber && <div style={{ fontSize: 15, color: '#065F46', fontWeight: 700 }}>{info.poNumber}</div>}
        </div>

        {/* No `capture` attribute — see MobileUploadPage.tsx's own comment on why. */}
        <input
          ref={fref} type="file" accept="image/jpeg,image/png,image/tiff,application/pdf" multiple
          style={{ display: 'none' }} onChange={e => handleFiles(e.target.files)}
        />
        <button
          onClick={() => fref.current?.click()}
          style={{ width: '100%', padding: '16px', borderRadius: 12, border: 'none', background: 'var(--teal, #0B5FD6)', color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer', marginBottom: 10 }}
        >
          📷 Take photo / choose file
        </button>
        <div style={{ textAlign: 'center', fontSize: 11, color: '#9CA3AF', marginBottom: 16 }}>
          You can upload multiple pages, one after another
        </div>

        {uploads.length > 0 && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Uploads</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {uploads.map((u, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '8px 10px', borderRadius: 8, background: u.status === 'error' ? '#FEE2E2' : '#F9FAFB' }}>
                  <span>{u.status === 'uploading' ? '⏳' : u.status === 'done' ? '✅' : '❌'}</span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: u.status === 'error' ? '#991B1B' : '#374151' }}>{u.name}</span>
                  {u.status === 'error' && <span style={{ fontSize: 11, color: '#991B1B' }}>{u.message}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ textAlign: 'center', fontSize: 11, color: '#9CA3AF', marginTop: 20 }}>
          This link expires automatically. You can close this window once you're done uploading.
        </div>
      </div>
    </div>
  );
}
